import { Box, SpeedDial, SpeedDialAction, Typography } from '@mui/material';

import { Check, Cog, Times, Clipboard } from '@assets/svgs';
import { useApiEnvStore } from '@utils/api-client';
import { getTodaysHoliday } from '@theme/palettes/getPalette';
import { useOktaAuth } from '@okta/okta-react';
import { isNotProduction } from '@utils/isInLowerEnv';

export const DebugModeControls = () => {
  const [debugModeOptions, setDebugModeOption] = useApiEnvStore((state) => [
    state.debugModeOptions,
    state.setDebugModeOption,
  ]);
  const { authState } = useOktaAuth();

  const { showQuoteCodeNames, showExtraDetails, showQuoteFieldInformation } = debugModeOptions;

  // Check if the current route matches /quote/:id but not /quote/:id/review
  const isQuotePage = /^\/quote\/[^/]+$/.test(window.location.pathname);

  // Determine the right position based on the URL (localhost has a react-query devtool widget so we have to scoot it over more)
  // due to implementation of impersonate we need to scoot more over if on quote page
  let rightPosition = (isQuotePage ? 50 : 0) + (window.location.href.includes('localhost') ? 80 : 16);

  if (getTodaysHoliday()) {
    rightPosition += 50;
  }

  // Environment switching helpers - guarded in case window.lio is undefined.
  const getApiEnv = () => {
    try {
      return (window as any)?.lio?.getApiEnvironment?.() || '';
    } catch {
      return '';
    }
  };
  const setApiEnv = (env: string) => {
    try {
      (window as any)?.lio?.setApiEnvironment?.(env);
    } catch {
      // noop
    }
  };
  const currentEnv = getApiEnv();
  const envOptions: { label: string; value: string; show: boolean }[] = [
    { label: 'LOCAL', value: 'local', show: true },
    { label: 'INT', value: 'int', show: true },
    { label: 'QA', value: 'qa', show: true },
    { label: 'UAT', value: 'uat', show: true },
  ];

  return (
    <>
      <SpeedDial
        ariaLabel='Debug Mode Controls'
        sx={{
          position: 'fixed',
          bottom: 16,
          right: rightPosition,
          width: 40,
          height: 40,
          '& .MuiFab-primary': {
            backgroundColor: 'gray',
            width: 40,
            height: 40,
          },
        }}
        icon={<Cog />}
      >
        {/* TODO if this becomes > 2 put into json and render from a map */}
        <SpeedDialAction
          icon={
            showQuoteCodeNames ? (
              <Check width='14px' height='14px' />
            ) : (
              <Box
                sx={{
                  color: 'white',
                }}
              >
                <Times width='20px' height='10px' />
              </Box>
            )
          }
          tooltipTitle={
            <Box>
              <Typography variant='bodySmall'>Show Quote Codenames</Typography>
            </Box>
          }
          tooltipOpen
          onClick={() => {
            setDebugModeOption('showQuoteCodeNames', !showQuoteCodeNames);
          }}
          sx={{
            ...sxProps.action,
            '& .MuiFab-root': {
              backgroundColor: showQuoteCodeNames ? 'other.success' : 'other.error',
              ':hover': {
                backgroundColor: showQuoteCodeNames ? '#43ba81' : 'red',
              },
            },
          }}
        />
        <SpeedDialAction
          icon={
            showQuoteFieldInformation ? (
              <Check width='14px' height='14px' />
            ) : (
              <Box
                sx={{
                  color: 'white',
                }}
              >
                <Times width='20px' height='10px' />
              </Box>
            )
          }
          tooltipTitle={
            <Box>
              <Typography variant='bodySmall'>Show Quote Field Info</Typography>
            </Box>
          }
          tooltipOpen
          onClick={() => {
            setDebugModeOption('showQuoteFieldInformation', !showQuoteFieldInformation);
          }}
          sx={{
            ...sxProps.action,
            '& .MuiFab-root': {
              backgroundColor: showQuoteFieldInformation ? 'other.success' : 'other.error',
              ':hover': {
                backgroundColor: showQuoteFieldInformation ? '#43ba81' : 'red',
              },
            },
          }}
        />{' '}
        <SpeedDialAction
          icon={
            showExtraDetails ? (
              <Check width='14px' height='14px' />
            ) : (
              <Box
                sx={{
                  color: 'white',
                }}
              >
                <Times width='20px' height='10px' />
              </Box>
            )
          }
          tooltipTitle={
            <Box>
              <Typography variant='bodySmall'>Show Extra Details</Typography>
            </Box>
          }
          tooltipOpen
          onClick={() => {
            setDebugModeOption('showExtraDetails', !showExtraDetails);
          }}
          sx={{
            ...sxProps.action,
            '& .MuiFab-root': {
              backgroundColor: showExtraDetails ? 'other.success' : 'other.error',
              ':hover': {
                backgroundColor: showExtraDetails ? '#43ba81' : 'red',
              },
            },
          }}
        />{' '}
        <SpeedDialAction
          icon={<Clipboard width='20px' height='20px' />}
          tooltipTitle={
            <Box>
              <Typography variant='bodySmall'>Copy Auth Token</Typography>
            </Box>
          }
          tooltipOpen
          onClick={() => {
            navigator.clipboard.writeText(authState?.accessToken?.accessToken);
          }}
          sx={{
            ...sxProps.action,
            '& .MuiFab-root': {
              backgroundColor: 'gray',
              ':hover': {
                backgroundColor: 'background.main',
              },
            },
          }}
        />
      </SpeedDial>
      {/* Environment Selector SpeedDial */}
      {isNotProduction() && (
        <SpeedDial
          ariaLabel='API Environment'
          sx={{
            position: 'fixed',
            bottom: 16,
            right: rightPosition + 60, // offset so it doesn't overlap the first dial
            width: 40,
            height: 40,
            '& .MuiFab-primary': {
              backgroundColor: 'gray',
              width: 40,
              height: 40,
            },
          }}
          icon={
            <Typography variant='bodySmall' sx={{ fontWeight: 600 }}>
              ENV
            </Typography>
          }
        >
          {envOptions
            .filter((o) => o.show)
            .map((o) => {
              const active = currentEnv === o.value;
              return (
                <SpeedDialAction
                  key={o.value}
                  icon={
                    active ? (
                      <Check width='14px' height='14px' />
                    ) : (
                      <Box sx={{ color: 'white' }}>
                        <Times width='20px' height='10px' />
                      </Box>
                    )
                  }
                  tooltipTitle={
                    <Box>
                      <Typography variant='bodySmall'>Set Env: {o.label}</Typography>
                    </Box>
                  }
                  tooltipOpen
                  onClick={() => setApiEnv(o.value)}
                  sx={{
                    ...sxProps.action,
                    '& .MuiFab-root': {
                      backgroundColor: active ? 'other.success' : 'other.error',
                      ':hover': {
                        backgroundColor: active ? '#43ba81' : 'red',
                      },
                    },
                  }}
                />
              );
            })}
        </SpeedDial>
      )}
    </>
  );
};

const sxProps = {
  action: {
    '.MuiSpeedDialAction-staticTooltipLabel': {
      backgroundColor: 'gray',
      backgroundOpacity: 0.4,
      color: 'white !important',
      boxShadow: 'none',
      marginRight: 0,
      textWrap: 'nowrap',
      height: '30px',
      borderRadius: 3,
      display: 'flex',
      alignItems: 'center',
    },
  },
};
